'use client';

import { useEffect, useState } from 'react';
import { Laboratory } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LaboratoryErrorFallback } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-error-fallback.component';
import { getLaboratoryScheduleFromSearchApi } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-schedule-from-search-api.service';
import { ArrivalOrderModalContent } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-modal.component';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { LaboratoryExamsFlowKeys } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/laboratory-exams-flow.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';

interface LaboratorySchedulesStepProps {
  laboratory: Laboratory;
  procedureIds: string[];
  total: number;
}

export function LaboratorySchedulesStep({
  laboratory,
  procedureIds,
  total,
}: LaboratorySchedulesStepProps) {
  const facilityId = laboratory.id ?? '';
  const procedureIdsKey = JSON.stringify(procedureIds);
  const { requestPatientSelection } = usePatientSelection();
  const { onClose } = useProgressiveModal<LaboratoryExamsFlowKeys>();

  const [scheduleId, setScheduleId] = useState<string | undefined>();
  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(Boolean(facilityId));
  const [hasScheduleError, setHasScheduleError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!facilityId) {
      return;
    }

    let cancelled = false;

    setIsLoadingSchedule(true);
    setScheduleId(undefined);
    setPartnerId(undefined);
    setHasScheduleError(false);

    getLaboratoryScheduleFromSearchApi({ facilityId, procedureIds: JSON.parse(procedureIdsKey) as string[] })
      .then((result) => {
        if (!cancelled) {
          setScheduleId(result.scheduleId ?? undefined);
          setPartnerId(result.partnerId ?? undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasScheduleError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSchedule(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, procedureIdsKey, reloadToken]);

  const handleSelectSlot = () => {
    onClose();
    requestPatientSelection({});
  };

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL}
    >
      <ArrivalOrderModalContent
        scheduleId={scheduleId}
        entityName={resolveLaboratoryName(laboratory)}
        entityAvatar={laboratory.main?.avatar}
        entityType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
        partnerId={partnerId ?? ''}
        price={total}
        slotFilters={{}}
        isLoadingSchedule={isLoadingSchedule}
        onSelectSlot={handleSelectSlot}
        errorContent={hasScheduleError
          ? (
              <LaboratoryErrorFallback
                message="Não foi possível carregar os horários."
                laboratoryId={facilityId}
                dataTestId={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL_ERROR}
                onRetry={() => setReloadToken((current) => current + 1)}
              />
            )
          : undefined}
      />
    </div>
  );
}
